import {
  AuthorProfile,
  ContentEntry,
  DeliverableP9,
  FinancialTerm,
  SearchPerformanceObservation,
  SeoOpportunity,
  SeoPageInstance,
} from '../types';

// -------------------------------------------------------------
// CERTIFIED DELIVERABLES FOR PROMPT 09
// -------------------------------------------------------------

export const DELIVERABLES_P9: DeliverableP9[] = [
  {
    code: 'P09-DEL-01',
    titleEn: 'YMYL Trust Layer & E-E-A-T Editorial Governance',
    titleFr: 'Couche de Confiance YMYL & Gouvernance Éditoriale E-E-A-T',
    category: 'YMYL Trust & Editorial Governance',
    summary:
      'Verified author & reviewer credentials, explicit WHO/HOW/WHY disclosures, Tier 1 official sourcing policy, and transparent financial correction audit logs.',
    details: [
      'Verified author & credential management (CFA, CPA, Tax Specialist credentials with verification status)',
      'Mandatory financial reviewer sign-off for HIGH & CRITICAL_FINANCIAL YMYL risk content',
      'Visible correction notes and immutable revision logs (no silent financial rewrites)',
      'Tier 1/2/3/4 Sourcing Policy tying every regulatory claim to official central bank/regulator sources',
      'Editorial independence disclosure & commercial transparency policy',
    ],
    architectureNotes:
      'Implements WHO/HOW/WHY metadata schema on all editorial entries. Enforces dual-author review for tax/lending guides.',
  },
  {
    code: 'P09-DEL-02',
    titleEn: 'YamooPay Financial Content Management System (CMS)',
    titleFr: 'CMS Éditorial Financier YamooPay',
    category: 'Content CMS & Author/Reviewer Engine',
    summary:
      'Integrated publishing engine with structured content blocks, adaptive editorial workflow states, source citation manager, and preview modes.',
    details: [
      'Multi-stage workflow: DRAFT → FACT_CHECK → FINANCIAL_REVIEW → SEO_REVIEW → APPROVED → PUBLISHED',
      'Structured content blocks (Calculation Examples, Data Tables, Method Notes, Embedded Tools)',
      'Content Freshness Engine tracking nextReviewAt with event-driven invalidation on tax/FX rule changes',
      'Desktop & Mobile responsive CMS preview with Schema JSON-LD & Open Graph inspector',
      'Content Dependency Graph linking articles to underlying RuleSets, DataSources, and Tools',
    ],
    architectureNotes:
      'CMS model isolates editorial text from functional calculators while maintaining bidirectional deep links.',
  },
  {
    code: 'P09-DEL-03',
    titleEn: 'Programmatic SEO Quality Gate & Indexability Governance',
    titleFr: 'Garde-Fou Programmatic SEO & Gouvernance d’Indexabilité',
    category: 'Programmatic SEO & Quality Gate',
    summary:
      'Strict quality gate filtering programmatic pages, preventing Scaled Content Abuse via data readiness checks and uniqueness thresholds.',
    details: [
      'Data-Driven Quality Gate: Only index pages meeting data completeness & uniqueness thresholds',
      'Quality-Gated Noindex: Retains utility pages for users without polluting search engine indexes',
      'Anti-Spam Safeguards: Strictly forbids template token-swapping without localized financial depth',
      'Emergency Indexing Kill Switch: Instantly toggles noindex for malfunctioning programmatic cohorts',
      'Page Factory versioning & canary deployment strategy for global page cohorts',
    ],
    architectureNotes:
      'Evaluates DataReadinessScore & UniquenessScore before injecting canonical & sitemap entries.',
  },
  {
    code: 'P09-DEL-04',
    titleEn: 'Search Console Intelligence & Organic Growth Command Center',
    titleFr: 'Intelligence Search Console & Centre de Commandement SEO',
    category: 'Search Console Intelligence & Analytics',
    summary:
      'Real-time Search Console API integration monitoring clicks, impressions, CTR, average position, cannibalization, and decay alerts.',
    details: [
      'Idempotent daily performance snapshot ingestion (clicks, impressions, CTR, position, device, query)',
      'Organic Opportunity Engine detecting High Impressions / Low CTR & Position 4–15 quick wins',
      'Cannibalization & Content Decay Detection with automated consolidation suggestions',
      'Privacy-Safe Funnel Tracking: Search Impression → Click → Tool Launch → Calculation Completed',
      'SEO Experiment Framework tracking title/content variants across page cohorts',
    ],
    architectureNotes:
      'Raw GSC observations stored in SearchPerformanceObservation table with privacy-safe aggregation.',
  },
  {
    code: 'P09-DEL-05',
    titleEn: 'Internationalization, Multi-Locale & Hreflang Graph',
    titleFr: 'Internationalisation & Graphe Hreflang Multi-Région',
    category: 'Internationalization & Hreflang Graph',
    summary:
      'Precise multi-locale URL architecture with self-referencing reciprocal hreflang alternate maps, x-default fallback, and true localization.',
    details: [
      'Distinct URL structure per language/region (/en, /fr, /es, /en-US, /en-GB, /fr-CA)',
      'Reciprocal Hreflang Graph generator validating exact bidirectional canonical links',
      'Global Financial Terminology Glossary ensuring accurate localized terms across jurisdictions',
      'Region Switcher vs Language Switcher separation preserving clean crawl paths',
      'Noindex Machine Drafts: Unreviewed automated translations are strictly quality-gated from search',
    ],
    architectureNotes:
      'Generates exact hreflang maps in XML sitemaps and JSON-LD headers without cross-domain canonical mismatch.',
  },
  {
    code: 'P09-DEL-06',
    titleEn: 'Schema.org Registry, Sitemaps & Technical SEO Engine',
    titleFr: 'Registre Schema.org, Sitemaps & Moteur SEO Technique',
    category: 'Schema.org Registry & Technical SEO',
    summary:
      'Dynamic Schema.org JSON-LD generator, segmented XML sitemap index, robots.txt management, and technical SEO regression suite.',
    details: [
      'Schema.org Registry generating Organization, BreadcrumbList, Article, ProfilePage, FinancialProduct',
      'JSON-LD matches visible on-page content 1:1 to guarantee zero Schema spam',
      'Segmented XML Sitemap Index (<50,000 URLs per file) filtered strictly to canonical indexable URLs',
      'Lastmod Governance: Updates lastmod timestamp only on significant financial/content modifications',
      'Automated Technical SEO CI/CD Audit verifying canonicals, robots directives, and broken links',
    ],
    architectureNotes:
      'Provides instant JSON-LD inspection modal and structured data validator directly in admin controls.',
  },
];

// -------------------------------------------------------------
// AUTHOR & REVIEWER PROFILES (E-E-A-T TRUST LAYER)
// -------------------------------------------------------------

export const AUTHOR_PROFILES: AuthorProfile[] = [
  {
    id: 'auth_dubois_01',
    slug: 'alexandre-dubois',
    name: 'Dr. Alexandre Dubois',
    role: 'Chief Global Economist & FX Intelligence Lead',
    bio: 'Former ECB monetary policy research fellow with 14+ years specializing in cross-border capital flows, mid-market exchange rate mechanics, and central bank liquidity.',
    expertiseAreas: [
      'Foreign Exchange Mechanics',
      'Central Bank Interventions',
      'Cross-Border Payments',
      'Macroeconomic Forecasting',
    ],
    credentials: [
      'Ph.D. Quantitative Economics (Sorbonne / LSE)',
      'Chartered Financial Analyst (CFA®)',
    ],
    credentialVerificationStatus: 'VERIFIED',
    profileImage: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=256&q=80',
    externalProfiles: {
      linkedin: 'https://linkedin.com/in/alexandre-dubois-yamoo',
      twitter: 'https://x.com/adubois_fx',
    },
    active: true,
  },
  {
    id: 'auth_jenkins_02',
    slug: 'sarah-jenkins',
    name: 'Sarah Jenkins, CPA',
    role: 'Senior Tax & Payroll Technical Reviewer',
    bio: 'Certified Public Accountant with 12 years auditing multi-state US paycheck withholding, California FTB compliance, UK PAYE tax bands, and expat income tax treaties.',
    expertiseAreas: [
      'US Federal & State Tax (W-2, 1099)',
      'UK PAYE & National Insurance',
      'Canadian Federal & Provincial Tax',
      'Expat Double Taxation Treaties',
    ],
    credentials: [
      'Certified Public Accountant (CPA — California State Board)',
      'M.S. Taxation (Golden Gate University)',
    ],
    credentialVerificationStatus: 'VERIFIED',
    profileImage: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=256&q=80',
    externalProfiles: {
      linkedin: 'https://linkedin.com/in/sarah-jenkins-cpa',
    },
    active: true,
  },
  {
    id: 'auth_vance_03',
    slug: 'marcus-vance',
    name: 'Marcus Vance, CFA',
    role: 'Senior Lending & Mortgage Strategy Director',
    bio: 'Specialist in mortgage compounding conventions, PITI housing ratios, APR vs APRC disclosure regulations, and credit refinancing risk modeling.',
    expertiseAreas: [
      'Mortgage Amortization Math',
      'US & European Lending Regulations (TILA, APRC)',
      'Refinancing & Prepayment Models',
      'Credit Risk Analysis',
    ],
    credentials: [
      'Chartered Financial Analyst (CFA®)',
      'B.S. Mathematical Finance (NYU Stern)',
    ],
    credentialVerificationStatus: 'VERIFIED',
    profileImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=256&q=80',
    externalProfiles: {
      linkedin: 'https://linkedin.com/in/marcus-vance-cfa',
    },
    active: true,
  },
];

// -------------------------------------------------------------
// SAMPLE FINANCIAL GUIDES & CONTENT ENTRIES
// -------------------------------------------------------------

export const SAMPLE_GUIDES: ContentEntry[] = [
  {
    id: 'guide_global_vat_changes_2026',
    contentType: 'GUIDE',
    slug: 'understanding-global-vat-changes-2026',
    locale: 'en',
    market: 'GLOBAL',
    status: 'PUBLISHED',
    title: 'Understanding Global VAT Changes',
    summary:
      'A comprehensive guide to value-added tax adjustments across major economies, cross-border digital services rules, and e-invoicing mandates.',
    bodyMarkdown: `
# Understanding Global VAT Changes

Value-Added Tax (VAT) and Goods and Services Tax (GST) regimes continue to undergo structural transformation worldwide as governments modernize revenue collection for digital commerce and cross-border digital services.

---

## 1. Key Value-Added Tax Shifts Across Major Jurisdictions

Across the European Union, the United Kingdom, GCC economies, and Asia-Pacific, fiscal authorities are focusing on real-time transaction reporting and standard rate adjustments:

* **European Union (ViDA Initiative)**: Phased implementation of the VAT in the Digital Age package, introducing standardized digital reporting and continuous transaction controls across all 27 member states.
* **United Kingdom**: Standard 20% rate maintained, with refined threshold rules for small businesses and digital platform operator reporting mandates.
* **GCC Region**: UAE and Saudi Arabia enforcing strict ZATCA Phase 2 e-invoicing integration and cross-border B2B validation rules.
* **Digital Goods & Software**: Over 90 jurisdictions now require non-resident remote sellers to register and collect destination-based VAT/GST on consumer electronic services.

---

## 2. Standard vs Reduced VAT Rates by Region

| Jurisdiction | Standard VAT/GST | Reduced Rates | Key Exceptions |
| :--- | :--- | :--- | :--- |
| **United Kingdom (HMRC)** | 20.0% | 5.0%, 0.0% | Food, books, children's clothing zero-rated |
| **France (DGFiP)** | 20.0% | 10.0%, 5.5%, 2.1% | Essential foods, public transit, cultural goods |
| **Germany (BZSt)** | 19.0% | 7.0% | Basic groceries, agricultural products |
| **Canada (CRA - GST/HST)** | 5.0% - 15.0% | Provincial variations | Zero-rated medical supplies, basic groceries |
| **Australia (ATO - GST)** | 10.0% | 0.0% (GST-free) | Basic food, health services, education |

---

## 3. Practical Calculation Formula

Standard output VAT is calculated mathematically as:
\`VAT Amount = Net Amount × (VAT Rate / 100)\`
\`Gross Amount = Net Amount + VAT Amount\`

To extract VAT from a gross total:
\`Net Amount = Gross Amount / (1 + (VAT Rate / 100))\`
\`Extracted VAT = Gross Amount - Net Amount\`
`,
    authorId: 'auth_jenkins_02',
    reviewerId: 'auth_dubois_01',
    publishedAt: '2026-02-10T08:00:00Z',
    updatedAt: '2026-08-20T12:00:00Z',
    nextReviewAt: '2026-12-31T00:00:00Z',
    riskLevel: 'HIGH',
    sources: [
      {
        id: 'src_oecd_vat_2026',
        contentId: 'guide_global_vat_changes_2026',
        sourceId: 'OECD_CONSUMPTION_TAX_TRENDS',
        citationLabel: 'OECD Consumption Tax Trends & Global VAT Policy Overview',
        referenceUrl: 'https://www.oecd.org/tax/consumption-tax-trends.htm',
        tier: 'TIER_1_OFFICIAL',
        retrievedAt: '2026-02-01T10:00:00Z',
        verifiedAt: '2026-08-20T10:00:00Z',
      },
    ],
    corrections: [],
    seoMetadata: {
      title: 'Understanding Global VAT Changes — YamooPay Insights',
      description:
        'A comprehensive guide to value-added tax adjustments across major economies, cross-border digital services rules, and e-invoicing mandates.',
      canonicalUrl: 'https://yamoopay.com/en/guides/understanding-global-vat-changes-2026',
      robotsDirective: 'index, follow',
      hreflangMap: {
        'en-US': 'https://yamoopay.com/en/guides/understanding-global-vat-changes-2026',
        'x-default': 'https://yamoopay.com/en/guides/understanding-global-vat-changes-2026',
      },
      keywords: ['global VAT changes', 'VAT rates 2026', 'digital services VAT', 'VAT calculation formulas'],
    },
    topicCluster: 'Tax & Compliance',
    embeddedToolId: 'everyday-command-center',
    faqs: [
      {
        question: 'How do cross-border digital VAT rules apply to software subscriptions?',
        answer:
          'Under destination-principle rules in the EU, UK, and most major economies, VAT is charged at the rate where the consumer is resident, not where the vendor is located.',
      },
    ],
  },
  {
    id: 'guide_mortgage_rates_deep_dive_2026',
    contentType: 'LOAN',
    slug: 'mortgage-rates-deep-dive-2026',
    locale: 'en',
    market: 'GLOBAL',
    status: 'PUBLISHED',
    title: 'Mortgage rates deep dive',
    summary:
      'Analyzing current trends in global lending and what it means for buyers. Breakdown of fixed vs variable rates, central bank paths, and amortization strategy.',
    bodyMarkdown: `
# Mortgage Rates Deep Dive: Global Lending Trends & Amortization

Navigating mortgage financing requires understanding how benchmark central bank rates (Federal Reserve, ECB, Bank of England) translate into retail lending margins and long-term repayment costs.

---

## 1. Fixed vs Variable Rate Mechanisms

* **Fixed-Rate Mortgages**: Insulate borrowers from interest rate volatility by locking the nominal rate for the loan tenure (15, 20, or 30 years in the US; 2 to 10 years in Europe and the UK).
* **Variable / Adjustable-Rate Mortgages (ARM)**: Benchmark to interbank indices (SOFR, EURIBOR, SONIA) plus a lender margin, adjusting periodically.

---

## 2. How Compounding & Amortization Impact Lifetime Cost

Mortgage amortization follows the standard actuarial monthly payment formula:
\`M = P × [r(1+r)^n] / [(1+r)^n - 1]\`

Where:
* \`M\` = Monthly principal and interest payment
* \`P\` = Total loan principal borrowed
* \`r\` = Monthly interest rate (Annual Rate ÷ 12)
* \`n\` = Total number of monthly payments (Years × 12)

Because interest is calculated on the declining principal balance, early payments predominantly cover interest charges. Accelerating principal payments early dramatically reduces total interest over the life of the loan.
`,
    authorId: 'auth_vance_03',
    reviewerId: 'auth_dubois_01',
    publishedAt: '2026-03-01T10:00:00Z',
    updatedAt: '2026-08-22T14:00:00Z',
    riskLevel: 'HIGH',
    sources: [
      {
        id: 'src_bis_lending_2026',
        contentId: 'guide_mortgage_rates_deep_dive_2026',
        sourceId: 'BIS_MORTGAGE_MARKET_REVIEW',
        citationLabel: 'Bank for International Settlements (BIS) Global Residential Mortgage Review',
        referenceUrl: 'https://www.bis.org',
        tier: 'TIER_1_OFFICIAL',
        retrievedAt: '2026-03-01T10:00:00Z',
        verifiedAt: '2026-08-22T14:00:00Z',
      },
    ],
    corrections: [],
    seoMetadata: {
      title: 'Mortgage Rates Deep Dive — YamooPay Insights',
      description:
        'Analyzing current trends in global lending and what it means for buyers. Learn amortization math, fixed vs variable rates, and interest savings.',
      canonicalUrl: 'https://yamoopay.com/en/guides/mortgage-rates-deep-dive-2026',
      robotsDirective: 'index, follow',
      hreflangMap: {
        'en-US': 'https://yamoopay.com/en/guides/mortgage-rates-deep-dive-2026',
        'x-default': 'https://yamoopay.com/en/guides/mortgage-rates-deep-dive-2026',
      },
      keywords: ['mortgage rates deep dive', 'amortization formula', 'fixed vs variable mortgage', 'mortgage interest calculation'],
    },
    topicCluster: 'Loans & Mortgages',
    embeddedToolId: 'loan-engine',
    faqs: [
      {
        question: 'How much interest can an extra payment save on a 30-year mortgage?',
        answer:
          'Contributing an extra $200 per month on a $400,000 mortgage at 6.0% can reduce the loan term by over 4.5 years and save more than $75,000 in total interest.',
      },
    ],
  },
  {
    id: 'guide_future_digital_payments_2026',
    contentType: 'TRANSFER',
    slug: 'the-future-of-digital-payments-2026',
    locale: 'en',
    market: 'GLOBAL',
    status: 'PUBLISHED',
    title: 'The Future of Digital Payments',
    summary:
      'How cross-border transactions are evolving for the modern workforce. Analysis of real-time rails, interbank settlement, and transparent FX margins.',
    bodyMarkdown: `
# The Future of Digital Payments: Cross-Border Velocity & Transparency

Cross-border payments are transitioning from legacy multi-hop correspondent banking (SWIFT MT103) toward instant, transparent, and multi-currency rails (ISO 20022, UPI-PayNow bilateral links, and real-time ledger settlement).

---

## 1. The Cost Breakdown of International Payments

The G20 Roadmap for Enhancing Cross-Border Payments aims to reduce average global remittance fees below 3% by 2030. Traditional corridors still conceal fees within exchange rate markups:

* **Retail Banks**: Often charge a 2.5% to 4.5% FX spread over the institutional mid-market rate, plus $20–$45 in outbound wire and correspondent intermediary fees.
* **Modern Digital Transfer Specialists**: Operate domestic payment liquidity pools, matching currency flows and offering zero or near-zero FX markups with transparent flat fees.

---

## 2. Comparing Settlement Speeds Across Modern Rails

* **Instant Rails (IMPS, Pix, FedNow, SEPA Instant)**: Settle funds in seconds 24/7/365.
* **Direct Bank-to-Bank Links**: Settle within hours with full tracking visibility.
* **Legacy Correspondent Networks**: Take 2 to 5 business days with opaque deduction charges.
`,
    authorId: 'auth_dubois_01',
    reviewerId: 'auth_jenkins_02',
    publishedAt: '2026-03-15T09:00:00Z',
    updatedAt: '2026-08-25T10:00:00Z',
    riskLevel: 'HIGH',
    sources: [
      {
        id: 'src_cpmis_g20_2026',
        contentId: 'guide_future_digital_payments_2026',
        sourceId: 'CPMI_G20_CROSS_BORDER_PAYMENTS',
        citationLabel: 'Committee on Payments and Market Infrastructures (CPMI) — G20 Cross-Border Payments Target Metrics',
        referenceUrl: 'https://www.bis.org/cpmi/',
        tier: 'TIER_1_OFFICIAL',
        retrievedAt: '2026-03-15T10:00:00Z',
        verifiedAt: '2026-08-25T10:00:00Z',
      },
    ],
    corrections: [],
    seoMetadata: {
      title: 'The Future of Digital Payments — YamooPay Insights',
      description:
        'How cross-border transactions are evolving for the modern workforce. Analysis of real-time rails, interbank settlement, and transparent FX margins.',
      canonicalUrl: 'https://yamoopay.com/en/guides/the-future-of-digital-payments-2026',
      robotsDirective: 'index, follow',
      hreflangMap: {
        'en-US': 'https://yamoopay.com/en/guides/the-future-of-digital-payments-2026',
        'x-default': 'https://yamoopay.com/en/guides/the-future-of-digital-payments-2026',
      },
      keywords: ['future of digital payments', 'cross-border transfers', 'remittance fees', 'mid-market exchange rates'],
    },
    topicCluster: 'International Transfers',
    embeddedToolId: 'money-transfers',
    faqs: [
      {
        question: 'What is the mid-market rate in international money transfers?',
        answer:
          'The mid-market rate is the midpoint between the buy and sell prices of two currencies on wholesale global markets. It is the real, uninflated exchange rate.',
      },
    ],
  },
  {
    id: 'guide_california_salary_2026',
    contentType: 'SALARY',
    slug: 'california-salary-tax-guide-2026',
    locale: 'en',
    market: 'US-CA',
    status: 'PUBLISHED',
    title: 'California Paycheck & Income Tax Guide (2026 IRS & FTB Brackets)',
    summary:
      'Comprehensive breakdown of California salary deductions including 2026 federal brackets, state marginal tax rates up to 13.3%, SDI, FICA, and net take-home pay formulas.',
    bodyMarkdown: `
# California Paycheck & Income Tax Guide (2026)

Understanding your net take-home pay in California requires accounting for both **federal IRS income tax brackets** and California's progressive **Franchise Tax Board (FTB) state income tax rates**, which range from 1% to 13.3%.
`,
    authorId: 'auth_jenkins_02',
    reviewerId: 'auth_dubois_01',
    publishedAt: '2026-01-15T08:00:00Z',
    updatedAt: '2026-08-15T12:00:00Z',
    nextReviewAt: '2026-12-31T00:00:00Z',
    riskLevel: 'CRITICAL_FINANCIAL',
    sources: [],
    corrections: [],
    seoMetadata: {
      title: 'California Salary Guide 2026',
      description: 'California salary calculations',
      canonicalUrl: 'https://yamoopay.com/en/guides/california-salary-tax-guide-2026',
      robotsDirective: 'index, follow',
      hreflangMap: {
        'en-US': 'https://yamoopay.com/en/guides/california-salary-tax-guide-2026',
      },
    },
    topicCluster: 'Salary & Income Tax',
    embeddedToolId: 'salary-engine',
  },
];

// -------------------------------------------------------------
// SEARCH CONSOLE OBSERVATIONS & OPPORTUNITY DATA
// -------------------------------------------------------------

export const SEARCH_OBSERVATIONS: SearchPerformanceObservation[] = [
  {
    id: 'gsc_01',
    date: '2026-08-23',
    pageUrl: 'https://yamoopay.com/en/guides/california-salary-tax-guide-2026',
    query: 'california salary calculator 2026',
    country: 'USA',
    device: 'DESKTOP',
    clicks: 1420,
    impressions: 18500,
    ctr: 0.0767,
    position: 2.8,
  },
  {
    id: 'gsc_02',
    date: '2026-08-23',
    pageUrl: 'https://yamoopay.com/en/guides/us-to-india-money-transfer-guide',
    query: 'send money to india usd to inr wise',
    country: 'USA',
    device: 'MOBILE',
    clicks: 980,
    impressions: 14200,
    ctr: 0.0690,
    position: 3.4,
  },
  {
    id: 'gsc_03',
    date: '2026-08-23',
    pageUrl: 'https://yamoopay.com/en/currencies/usd-eur',
    query: 'usd to eur exchange rate today',
    country: 'USA',
    device: 'DESKTOP',
    clicks: 2840,
    impressions: 31000,
    ctr: 0.0916,
    position: 1.9,
  },
  {
    id: 'gsc_04',
    date: '2026-08-23',
    pageUrl: 'https://yamoopay.com/en/guides/apr-vs-aprc-loan-amortization-explained',
    query: 'apr vs nominal rate mortgage difference',
    country: 'GBR',
    device: 'DESKTOP',
    clicks: 430,
    impressions: 8900,
    ctr: 0.0483,
    position: 6.2,
  },
  {
    id: 'gsc_05',
    date: '2026-08-23',
    pageUrl: 'https://yamoopay.com/en/salary/us/california',
    query: 'san francisco tech salary net pay calculator',
    country: 'USA',
    device: 'MOBILE',
    clicks: 1150,
    impressions: 12400,
    ctr: 0.0927,
    position: 2.1,
  },
];

export const SEO_OPPORTUNITIES: SeoOpportunity[] = [
  {
    id: 'opp_01',
    title: 'High Impressions / Position 6.2 on APR vs APRC Guide',
    pageUrl: 'https://yamoopay.com/en/guides/apr-vs-aprc-loan-amortization-explained',
    targetQuery: 'apr vs nominal rate mortgage difference',
    currentPosition: 6.2,
    potentialClicks: 1250,
    opportunityScore: 88,
    recommendationType: 'TITLE_OPTIMIZATION',
    status: 'OPEN',
  },
  {
    id: 'opp_02',
    title: 'Content Refresh: Add 2026 H1-B Tax Exemption Notes to CA Salary Guide',
    pageUrl: 'https://yamoopay.com/en/guides/california-salary-tax-guide-2026',
    targetQuery: 'california paycheck after taxes 2026',
    currentPosition: 2.8,
    potentialClicks: 840,
    opportunityScore: 92,
    recommendationType: 'CONTENT_REFRESH',
    status: 'OPEN',
  },
  {
    id: 'opp_03',
    title: 'Internal Linking: Connect Currency Pairs to Remittance Guides',
    pageUrl: 'https://yamoopay.com/en/currencies/usd-inr',
    targetQuery: 'usd to inr remittance comparison',
    currentPosition: 4.1,
    potentialClicks: 1600,
    opportunityScore: 85,
    recommendationType: 'INTERNAL_LINKING',
    status: 'IN_PROGRESS',
  },
];

// -------------------------------------------------------------
// PROGRAMMATIC PAGE INVENTORY & INDEXABILITY GOVERNANCE
// -------------------------------------------------------------

export const PROGRAMMATIC_PAGES: SeoPageInstance[] = [
  {
    id: 'prog_usd_eur',
    url: 'https://yamoopay.com/en/currencies/usd-eur',
    pageType: 'CURRENCY_PAIR',
    locale: 'en-US',
    market: 'GLOBAL',
    status: 'PUBLISHED',
    indexabilityStatus: 'INDEXABLE',
    canonicalUrl: 'https://yamoopay.com/en/currencies/usd-eur',
    hreflangGroup: {
      'en-US': 'https://yamoopay.com/en/currencies/usd-eur',
      'fr-FR': 'https://yamoopay.com/fr/devises/usd-eur',
      'es-ES': 'https://yamoopay.com/es/divisas/usd-eur',
      'x-default': 'https://yamoopay.com/en/currencies/usd-eur',
    },
    dataReadinessScore: 98,
    uniquenessScore: 95,
    searchClicks: 2840,
    impressions: 31000,
    ctr: 0.0916,
    position: 1.9,
    lastSignificantUpdate: '2026-08-24T00:00:00Z',
  },
  {
    id: 'prog_us_in_transfer',
    url: 'https://yamoopay.com/en/transfers/us-to-india',
    pageType: 'CORRIDOR',
    locale: 'en-US',
    market: 'US-IN',
    status: 'PUBLISHED',
    indexabilityStatus: 'INDEXABLE',
    canonicalUrl: 'https://yamoopay.com/en/transfers/us-to-india',
    hreflangGroup: {
      'en-US': 'https://yamoopay.com/en/transfers/us-to-india',
      'fr-FR': 'https://yamoopay.com/fr/transferts/usa-vers-inde',
      'x-default': 'https://yamoopay.com/en/transfers/us-to-india',
    },
    dataReadinessScore: 96,
    uniquenessScore: 92,
    searchClicks: 1980,
    impressions: 22400,
    ctr: 0.0883,
    position: 2.3,
    lastSignificantUpdate: '2026-08-20T10:00:00Z',
  },
  {
    id: 'prog_us_ca_salary',
    url: 'https://yamoopay.com/en/salary/us/california',
    pageType: 'SALARY_JURISDICTION',
    locale: 'en-US',
    market: 'US-CA',
    status: 'PUBLISHED',
    indexabilityStatus: 'INDEXABLE',
    canonicalUrl: 'https://yamoopay.com/en/salary/us/california',
    hreflangGroup: {
      'en-US': 'https://yamoopay.com/en/salary/us/california',
      'x-default': 'https://yamoopay.com/en/salary/us/california',
    },
    dataReadinessScore: 99,
    uniquenessScore: 98,
    searchClicks: 2150,
    impressions: 24800,
    ctr: 0.0866,
    position: 2.1,
    lastSignificantUpdate: '2026-08-15T12:00:00Z',
  },
  {
    id: 'prog_rare_pair_01',
    url: 'https://yamoopay.com/en/currencies/zmw-mvr',
    pageType: 'CURRENCY_PAIR',
    locale: 'en-US',
    market: 'GLOBAL',
    status: 'DRAFT',
    indexabilityStatus: 'QUALITY_NOINDEX',
    canonicalUrl: 'https://yamoopay.com/en/currencies/zmw-mvr',
    hreflangGroup: {
      'en-US': 'https://yamoopay.com/en/currencies/zmw-mvr',
    },
    dataReadinessScore: 42,
    uniquenessScore: 35,
    searchClicks: 0,
    impressions: 12,
    ctr: 0.0,
    position: 45.2,
    noindexReason: 'Insufficient historical FX volatility data & zero organic query volume.',
    lastSignificantUpdate: '2026-08-01T00:00:00Z',
  },
];

// -------------------------------------------------------------
// GLOBAL FINANCIAL TERMINOLOGY GLOSSARY
// -------------------------------------------------------------

export const GLOSSARY_TERMS: FinancialTerm[] = [
  {
    id: 'term_take_home_pay',
    termKey: 'TakeHomePay',
    canonicalConcept: 'Net Salary after all statutory payroll deductions',
    locale: 'en-US',
    localizedTerm: 'Take-Home Pay (Net Pay)',
    definition:
      'The actual net amount credited to an employee’s bank account on payday after statutory federal, state/provincial taxes, social security, Medicare, and voluntary pre-tax deductions.',
    market: 'Global',
    synonyms: ['Net Pay', 'Disposable Earnings', 'Salaire Net'],
    reviewer: 'Sarah Jenkins, CPA',
    relatedToolId: 'salary-engine',
  },
  {
    id: 'term_mid_market_rate',
    termKey: 'MidMarketRate',
    canonicalConcept: 'Interbank midpoint between buy and sell FX rates',
    locale: 'en-US',
    localizedTerm: 'Mid-Market Exchange Rate',
    definition:
      'The midpoint between global institutional buy and sell prices on wholesale currency markets. Represents the true, un-marked-up benchmark exchange rate.',
    market: 'Global',
    synonyms: ['Spot Rate', 'Interbank Rate', 'Real Exchange Rate', 'Taux Interbancaire'],
    reviewer: 'Dr. Alexandre Dubois',
    relatedToolId: 'currency-intelligence',
  },
  {
    id: 'term_aprc',
    termKey: 'APRC',
    canonicalConcept: 'Statutory Annual Percentage Rate of Charge',
    locale: 'en-US',
    localizedTerm: 'APRC (Annual Percentage Rate of Charge / TAEG)',
    definition:
      'The statutory effective annualized cost of borrowing, incorporating nominal interest, origination fees, valuation fees, mandatory insurance, and notary fees into a standardized percentage.',
    market: 'Global / EU / UK',
    synonyms: ['TAEG', 'Effective APR', 'Taux Annuel Effectif Global'],
    reviewer: 'Marcus Vance, CFA',
    relatedToolId: 'loan-engine',
  },
  {
    id: 'term_remittance_corridor',
    termKey: 'RemittanceCorridor',
    canonicalConcept: 'Bilateral cross-border money transfer route',
    locale: 'en-US',
    localizedTerm: 'Remittance Corridor',
    definition:
      'A specific pair of sending and receiving countries for cross-border money transfers (e.g., United States → India, United Kingdom → Nigeria).',
    market: 'Global',
    synonyms: ['Transfer Corridor', 'Payment Corridor'],
    reviewer: 'Dr. Alexandre Dubois',
    relatedToolId: 'money-transfers',
  },
];

// -------------------------------------------------------------
// METHODOLOGY DOCUMENTS
// -------------------------------------------------------------

export const METHODOLOGY_DOCS = [
  {
    id: 'meth_fx_01',
    title: 'YamooPay Exchange Rate & Mid-Market Data Methodology',
    summary:
      'How YamooPay aggregates institutional interbank liquidity feeds, computes 15-minute mid-market reference snapshots, and verifies rate freshness.',
    category: 'Currency & Exchange Rates',
    lastUpdated: '2026-08-20',
    keyPoints: [
      'Data blended from 3 Tier 1 central banks + 2 institutional liquidity pools',
      'Mid-market rate calculated as arithmetic mean of top-of-book bid/ask quotes',
      'Automated rate freshness checks with alert triggers on >0.5% spike anomaly',
    ],
  },
  {
    id: 'meth_salary_02',
    title: 'Paycheck & Income Tax Bracket Calculation Methodology',
    summary:
      'Deterministic statutory calculation logic for IRS Publication 15-T, state revenue boards, Canadian CRA, UK HMRC PAYE, and European social security tiers.',
    category: 'Salary & Income Tax',
    lastUpdated: '2026-08-15',
    keyPoints: [
      'Zero AI estimation — 100% statutory code enforcement',
      'Annual tax bracket tables updated within 24 hours of official revenue authority publication',
      'Dual verification by CPAs and tax technical reviewers prior to deployment',
    ],
  },
  {
    id: 'meth_lending_03',
    title: 'Mortgage Amortization, APR & APRC Calculation Methodology',
    summary:
      'Mathematical formulas for compound interest, monthly PITI housing expenses, APR fee amortization, and prepayment interest acceleration.',
    category: 'Loans & Lending',
    lastUpdated: '2026-08-12',
    keyPoints: [
      'Standard 30-day/360 and Actual/365 day-count compounding conventions',
      'US TILA Regulation Z and European EU Mortgage Credit Directive (2014/17/EU) compliance',
      'Iterative internal rate of return (IRR) solving for statutory APRC/TAEG percentages',
    ],
  },
];
